/**
 * WorkBuddy Cloud Storage facade（#89098）
 *
 * 生成应用只使用环境内固定的 `runtime` 逻辑 Bucket。物理 COS Bucket 与 envId
 * BasePath 由 TCB 管理，SDK 不感知；Bucket 创建/删除/清空属于可信控制面，
 * 不暴露给终端应用。
 */

import { CLOUD_MODULE_PATHS } from '../paths.js';
import type { CloudRuntimeConfig } from '../types.js';
import StorageFileApi from '../vendor/storage/packages/StorageFileApi.js';
import { DEFAULT_HEADERS } from '../vendor/storage/lib/constants.js';
import type { StorageClient } from '../vendor/storage/StorageClient.js';

export const RUNTIME_STORAGE_BUCKET = 'runtime';
export const DEFAULT_SIGNED_URL_TTL_SECONDS = 10 * 60;
export const MAX_SIGNED_URL_TTL_SECONDS = 60 * 60;

export type CloudStorageFileBody =
    | ArrayBuffer
    | ArrayBufferView
    | Blob
    | File
    | FormData
    | ReadableStream<Uint8Array>
    | URLSearchParams
    | string;

export interface CloudStorageFileOptions {
    cacheControl?: string;
    contentType?: string;
    upsert?: boolean;
    duplex?: string;
    metadata?: Record<string, unknown>;
}

export interface CloudStorageListOptions {
    limit?: number;
    offset?: number;
    sortBy?: { column?: string; order?: string };
    search?: string;
}

export interface CloudStorageCursorListOptions {
    limit?: number;
    prefix?: string;
    cursor?: string;
    with_delimiter?: boolean;
    sortBy?: {
        column: 'name' | 'updated_at' | 'created_at';
        order?: 'asc' | 'desc';
    };
}

export interface CloudStorageMetadata {
    eTag?: string;
    size?: number;
    mimetype?: string;
    cacheControl?: string;
    lastModified?: string;
    contentLength?: number;
    httpStatusCode?: number;
    [key: string]: unknown;
}

export interface CloudStorageFile {
    name: string;
    id: string | null;
    updated_at: string | null;
    created_at: string | null;
    last_accessed_at: string | null;
    metadata: CloudStorageMetadata | null;
}

export interface CloudStorageObjectInfo {
    id: string;
    version: string;
    name: string;
    bucketId: string;
    createdAt: string;
    size?: number;
    cacheControl?: string;
    contentType?: string;
    etag?: string;
    lastModified?: string;
    metadata?: CloudStorageMetadata;
}

export interface CloudStorageCursorObject {
    name: string;
    key?: string;
    id: string;
    updated_at: string;
    created_at: string;
    metadata: CloudStorageMetadata | null;
}

export interface CloudStorageCursorFolder {
    name: string;
    key?: string;
}

export interface CloudStorageListResult {
    hasNext: boolean;
    folders: CloudStorageCursorFolder[];
    objects: CloudStorageCursorObject[];
    nextCursor?: string;
}

export interface CloudStorageError {
    name: string;
    message: string;
    status?: number;
    statusCode?: string;
    code?: string;
    cause?: unknown;
}

export type CloudStorageResult<T> =
    | { data: T; error: null }
    | { data: null; error: CloudStorageError };

/** SDK 路径非法。请求尚未发出。 */
export class CloudStoragePathError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'CloudStoragePathError';
    }
}

export class WorkBuddyStorageDownload implements Promise<CloudStorageResult<Blob>> {
    readonly [Symbol.toStringTag] = 'WorkBuddyStorageDownload';

    constructor(
        private readonly blob: () => Promise<CloudStorageResult<Blob>>,
        private readonly stream: () => Promise<CloudStorageResult<ReadableStream>>,
    ) {}

    asStream(): Promise<CloudStorageResult<ReadableStream>> {
        return this.stream();
    }

    then<TResult1 = CloudStorageResult<Blob>, TResult2 = never>(
        onfulfilled?:
            | ((value: CloudStorageResult<Blob>) => TResult1 | PromiseLike<TResult1>)
            | null,
        onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
    ): Promise<TResult1 | TResult2> {
        return this.blob().then(onfulfilled, onrejected);
    }

    catch<TResult = never>(
        onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null,
    ): Promise<CloudStorageResult<Blob> | TResult> {
        return this.blob().catch(onrejected);
    }

    finally(onfinally?: (() => void) | null): Promise<CloudStorageResult<Blob>> {
        return this.blob().finally(onfinally);
    }
}

export class WorkBuddyStorageBucket {
    private readonly fileApi: StorageFileApi;

    constructor(fileApi: unknown) {
        this.fileApi = fileApi as StorageFileApi;
    }

    upload(
        path: string,
        body: CloudStorageFileBody,
        options?: CloudStorageFileOptions,
    ): Promise<CloudStorageResult<{ id: string; path: string; fullPath: string }>> {
        return mapStorageResult(
            this.fileApi.upload(validateObjectPath(path), body, options),
        );
    }

    update(
        path: string,
        body: CloudStorageFileBody,
        options?: CloudStorageFileOptions,
    ): Promise<CloudStorageResult<{ id: string; path: string; fullPath: string }>> {
        return mapStorageResult(
            this.fileApi.update(validateObjectPath(path), body, options),
        );
    }

    list(
        prefix = '',
        options?: CloudStorageListOptions,
    ): Promise<CloudStorageResult<CloudStorageFile[]>> {
        return mapStorageResult(
            this.fileApi.list(validateObjectPrefix(prefix), options),
        );
    }

    listPage(
        options?: CloudStorageCursorListOptions,
    ): Promise<CloudStorageResult<CloudStorageListResult>> {
        return mapStorageResult(
            this.fileApi.listV2({
                ...options,
                prefix: validateObjectPrefix(options?.prefix ?? ''),
            }),
        );
    }

    info(path: string): Promise<CloudStorageResult<CloudStorageObjectInfo>> {
        return mapStorageResult(this.fileApi.info(validateObjectPath(path)));
    }

    async exists(path: string): Promise<CloudStorageResult<boolean>> {
        const result = await this.fileApi.exists(validateObjectPath(path));
        if (result.error) {
            return { data: null, error: normalizeStorageError(result.error) };
        }
        return { data: result.data, error: null };
    }

    download(path: string): WorkBuddyStorageDownload {
        const builder = this.fileApi.download(validateObjectPath(path));
        return new WorkBuddyStorageDownload(
            () => mapStorageResult(builder),
            () => mapStorageResult(builder.asStream()),
        );
    }

    remove(paths: string[]): Promise<CloudStorageResult<CloudStorageFile[]>> {
        if (paths.length === 0) {
            throw new CloudStoragePathError('remove requires at least one object path.');
        }
        return mapStorageResult(this.fileApi.remove(paths.map(validateObjectPath)));
    }

    copy(
        fromPath: string,
        toPath: string,
    ): Promise<CloudStorageResult<{ path: string }>> {
        return mapStorageResult(
            this.fileApi.copy(validateObjectPath(fromPath), validateObjectPath(toPath)),
        );
    }

    move(
        fromPath: string,
        toPath: string,
    ): Promise<CloudStorageResult<{ message: string }>> {
        return mapStorageResult(
            this.fileApi.move(validateObjectPath(fromPath), validateObjectPath(toPath)),
        );
    }

    createSignedUrl(
        path: string,
        expiresIn = DEFAULT_SIGNED_URL_TTL_SECONDS,
    ): Promise<CloudStorageResult<{ signedUrl: string }>> {
        return mapStorageResult(
            this.fileApi.createSignedUrl(
                validateObjectPath(path),
                validateSignedURLTTL(expiresIn),
            ),
        );
    }

    createSignedUrls(
        paths: string[],
        expiresIn = DEFAULT_SIGNED_URL_TTL_SECONDS,
    ): Promise<CloudStorageResult<Array<{
        error: string | null;
        path: string | null;
        signedURL: string | null;
        signedUrl: string | null;
    }>>> {
        if (paths.length === 0) {
            throw new CloudStoragePathError('createSignedUrls requires at least one object path.');
        }
        return mapStorageResult(
            this.fileApi.createSignedUrls(
                paths.map(validateObjectPath),
                validateSignedURLTTL(expiresIn),
            ),
        );
    }

    createSignedUploadUrl(
        path: string,
        options?: { upsert?: boolean },
    ): Promise<CloudStorageResult<{ signedUrl: string; token: string; path: string }>> {
        return mapStorageResult(
            this.fileApi.createSignedUploadUrl(validateObjectPath(path), {
                upsert: options?.upsert ?? false,
            }),
        );
    }

    uploadToSignedUrl(
        path: string,
        token: string,
        body: CloudStorageFileBody,
        options?: CloudStorageFileOptions,
    ): Promise<CloudStorageResult<{ path: string; fullPath: string }>> {
        if (!token) {
            throw new CloudStoragePathError('signed upload token is required.');
        }
        return mapStorageResult(
            this.fileApi.uploadToSignedUrl(validateObjectPath(path), token, body, options),
        );
    }
}

export class WorkBuddyStorageModule extends WorkBuddyStorageBucket {
    private readonly runtime: WorkBuddyStorageBucket;

    constructor(client: unknown) {
        const storageClient = client as Pick<StorageClient, 'from'>;
        const runtimeApi = storageClient.from(RUNTIME_STORAGE_BUCKET);
        super(runtimeApi);
        this.runtime = new WorkBuddyStorageBucket(runtimeApi);
    }

    /**
     * 兼容 bucket-native 调用形态，但只允许固定的 `runtime`。
     * 终端应用不能枚举、创建或切换逻辑 Bucket。
     */
    from(bucketID: typeof RUNTIME_STORAGE_BUCKET = RUNTIME_STORAGE_BUCKET): WorkBuddyStorageBucket {
        if (bucketID !== RUNTIME_STORAGE_BUCKET) {
            throw new CloudStoragePathError(
                `Only the "${RUNTIME_STORAGE_BUCKET}" storage bucket is available.`,
            );
        }
        return this.runtime;
    }

    userPath(userID: string, relativePath: string): string {
        return scopedPath('users', userID, relativePath);
    }

    sharedPath(ownerID: string, relativePath: string): string {
        return scopedPath('shared', ownerID, relativePath);
    }
}

export function createStorageModule(
    config: CloudRuntimeConfig,
    fetch: typeof globalThis.fetch,
): WorkBuddyStorageModule {
    // 只接入文件 API，避免完整 StorageClient 引入未开放的 analytics 和 iceberg-js。
    const client = {
        from: (bucketID: string) => new StorageFileApi(
            `${config.endpoint}${CLOUD_MODULE_PATHS.storage}`, DEFAULT_HEADERS, bucketID, fetch,
        ),
    };
    return new WorkBuddyStorageModule(client);
}

function scopedPath(scope: 'users' | 'shared', ownerID: string, relativePath: string): string {
    const owner = validatePathSegment(ownerID, 'owner/user id');
    const relative = validatePath(relativePath, false);
    return validateObjectPath(`${scope}/${owner}/${relative}`);
}

export function validateObjectPath(path: string): string {
    const normalized = validatePath(path, false);
    if (!normalized.includes('/')) {
        throw new CloudStoragePathError(
            'Object paths must use users/<uid>/... or shared/<ownerUid>/....',
        );
    }
    const [scope, owner, ...rest] = normalized.split('/');
    if ((scope !== 'users' && scope !== 'shared') || !owner || rest.length === 0) {
        throw new CloudStoragePathError(
            'Object paths must use users/<uid>/... or shared/<ownerUid>/....',
        );
    }
    return normalized;
}

export function validateObjectPrefix(prefix: string): string {
    if (prefix === '') {
        return '';
    }
    const normalized = validatePath(prefix, true);
    const [scope, owner] = normalized.split('/');
    if (scope === 'shared' && !owner) {
        return 'shared';
    }
    if ((scope !== 'users' && scope !== 'shared') || !owner) {
        throw new CloudStoragePathError(
            'List prefixes must start with users/<uid> or shared/<ownerUid>.',
        );
    }
    return normalized;
}

function validatePath(path: string, allowTrailingSlash: boolean): string {
    if (typeof path !== 'string' || path.trim() === '') {
        throw new CloudStoragePathError('Storage path must be a non-empty relative path.');
    }
    let decoded = path.trim();
    for (let index = 0; index < 3; index += 1) {
        try {
            const next = decodeURIComponent(decoded);
            if (next === decoded) break;
            decoded = next;
        } catch {
            throw new CloudStoragePathError('Storage path contains invalid URL encoding.');
        }
    }
    if (
        decoded.startsWith('/')
        || decoded.includes('\\')
        || decoded.includes('\0')
        || decoded.includes('//')
        || (!allowTrailingSlash && decoded.endsWith('/'))
    ) {
        throw new CloudStoragePathError('Storage path is not a safe relative object path.');
    }
    const normalized = allowTrailingSlash ? decoded.replace(/\/+$/, '') : decoded;
    const segments = normalized.split('/');
    if (segments.some(segment => segment === '' || segment === '.' || segment === '..')) {
        throw new CloudStoragePathError('Storage path traversal is not allowed.');
    }
    return normalized;
}

function validatePathSegment(value: string, label: string): string {
    if (!value) {
        throw new CloudStoragePathError(`${label} is not a valid storage path segment.`);
    }
    let decoded = value;
    for (let index = 0; index < 3; index += 1) {
        try {
            const next = decodeURIComponent(decoded);
            if (next === decoded) break;
            decoded = next;
        } catch {
            throw new CloudStoragePathError(`${label} contains invalid URL encoding.`);
        }
    }
    if (
        decoded.includes('/')
        || decoded.includes('\\')
        || decoded.includes('\0')
        || decoded === '.'
        || decoded === '..'
    ) {
        throw new CloudStoragePathError(`${label} is not a valid storage path segment.`);
    }
    return decoded;
}

function validateSignedURLTTL(expiresIn: number): number {
    if (
        !Number.isInteger(expiresIn)
        || expiresIn <= 0
        || expiresIn > MAX_SIGNED_URL_TTL_SECONDS
    ) {
        throw new CloudStoragePathError(
            `Signed URL expiry must be an integer between 1 and ${MAX_SIGNED_URL_TTL_SECONDS} seconds.`,
        );
    }
    return expiresIn;
}

async function mapStorageResult<T>(
    source: PromiseLike<{ data: T | null; error: unknown | null }>,
): Promise<CloudStorageResult<T>> {
    const result = await source;
    if (result.error) {
        return { data: null, error: normalizeStorageError(result.error) };
    }
    if (result.data === null) {
        return {
            data: null,
            error: {
                name: 'CloudStorageResponseError',
                message: 'Storage API returned neither data nor an error.',
            },
        };
    }
    return { data: result.data, error: null };
}

function normalizeStorageError(error: unknown): CloudStorageError {
    if (error instanceof Error) {
        const source = error as Error & {
            status?: number;
            statusCode?: string;
            code?: string;
        };
        return {
            name: source.name,
            message: source.message,
            status: source.status,
            statusCode: source.statusCode,
            code: source.code,
            cause: error,
        };
    }
    return {
        name: 'CloudStorageError',
        message: String(error),
        cause: error,
    };
}
